import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary, Icon, useToast } from '@kollez/design-system';
import { apiGet } from '@/lib/api';
import { initialTableParams, toQueryString, type Paginated, type TableQueryParams } from '@/lib/tableQuery';
import { Pagination, SortHeader, TableSearch, ResetButton } from '@/components/DataTable';
import { NewPartnerDrawer } from '@/components/NewPartnerDrawer';
import { useCreateTpoPartner, type CreatePartnerBody } from '@/lib/queries';

/**
 * /tpo/partners — TPO's view of hiring partners scoped to this college's tenant.
 *
 * Uses the same server surface as /group/partners (both mounted on the same handler at
 * `/api/v1/{scope}/partners`); tenant scope is derived server-side from req.tenantScope,
 * so this page shows partners active at the TPO's college.
 */
interface Mou { _id: string; status: string; term?: { start?: string; end?: string } }
interface PartnerRow {
  _id: string;
  name: string;
  slug: string;
  industry?: string;
  scope: 'GLOBAL' | 'TENANT';
  status?: string;
  isVerified?: boolean;
  companySize?: string;
  headquarters?: { city?: string; state?: string };
  mousWithThisGroup?: Mou[];
}

const initial: TableQueryParams = initialTableParams({ sort: 'name', order: 'asc' });

const STATUS_OPTIONS = [
  ['', 'Any status'],
  ['Active', 'Active'],
  ['Prospect', 'Prospect'],
  ['Blacklisted', 'Blacklisted'],
] as const;

export function TpoPartnersPage() {
  const nav = useNavigate();
  const toast = useToast();
  const [params, setParams] = useState<TableQueryParams>(initial);
  const [qDraft, setQDraft] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState<CreatePartnerBody>({
    name: '', type: 'Product_Company', industry: '', companySize: '', website: '', description: '',
    admin: { fullName: '', email: '', phone: '' },
  });
  const [inviteLinkModal, setInviteLinkModal] = useState<string | null>(null);
  const createPartner = useCreateTpoPartner();

  useEffect(() => {
    const t = setTimeout(() => setParams((p) => ({ ...p, q: qDraft || undefined, page: 1 })), 300);
    return () => clearTimeout(t);
  }, [qDraft]);

  const query = useQuery({
    queryKey: ['tpo', 'partners', params],
    queryFn: () => apiGet<Paginated<PartnerRow>>(`/tpo/partners?${toQueryString(params)}`),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.rows ?? [];
  const kpis = {
    total: query.data?.total ?? 0,
    withMou: rows.filter((r) => (r.mousWithThisGroup?.length ?? 0) > 0).length,
    verified: rows.filter((r) => r.isVerified).length,
    global: rows.filter((r) => r.scope === 'GLOBAL').length,
  };

  const onSort = (field: string) => setParams((p) => ({
    ...p,
    sort: field,
    order: p.sort === field && p.order === 'asc' ? 'desc' : 'asc',
    page: 1,
  }));

  const reset = () => { setQDraft(''); setParams(initial); };
  const filtersDirty = !!(params.q || params.status || params.sort !== initial.sort || params.order !== initial.order);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Partners</h1>
          <p className="subhead">Hiring partners active at your college. Signed MoUs unlock private drives; global partners are always available.</p>
        </div>
        <div className="ph-actions">
          {/* Deep-audit fix (Page 35/N): 'plus' isn't in the Icon
              registry (verified via grep). Every render produced a
              broken slot next to 'New partner'. Replaced with
              'file-plus' which reads correctly for the action. */}
          <button className="btn btn-primary" onClick={() => setNewOpen(true)}>
            <Icon name="file-plus" size={14} />New partner
          </button>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <div className="card kpi"><span className="kpi-label">All partners</span><span className="kpi-value">{kpis.total}</span></div>
        <div className="card kpi" data-tone="success"><span className="kpi-label">With signed MoU</span><span className="kpi-value">{kpis.withMou}</span></div>
        <div className="card kpi"><span className="kpi-label">Verified</span><span className="kpi-value">{kpis.verified}</span></div>
        <div className="card kpi"><span className="kpi-label">Global partners</span><span className="kpi-value">{kpis.global}</span></div>
      </div>

      <div className="card">
        <div className="filterbar">
          <TableSearch value={qDraft} onChange={setQDraft} placeholder="Partner name" />
          <select className="select" value={String(params.status ?? '')} onChange={(e) => setParams((p) => ({ ...p, status: e.target.value || undefined, page: 1 }))}>
            {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <ResetButton onReset={reset} disabled={!filtersDirty} />
          <span className="fb-count">{query.data ? `${query.data.total} total` : ''}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={query.isLoading}
          error={query.error}
          empty={!!(query.data && query.data.total === 0)}
          emptyTitle="No partners yet"
          emptyHint="Click 'New partner' to onboard your first hiring partner."
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <SortHeader label="Partner" field="name" currentSort={params.sort ?? null} currentOrder={params.order ?? 'asc'} onSort={onSort} />
                  <th className="hide-mobile">Industry</th>
                  <th className="hide-mobile">HQ</th>
                  <th>Scope</th>
                  <th>MoUs w/ college</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id} onClick={() => nav(`/tpo/partners/${r._id}`)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className="cell-title">{r.name}</div>
                      <div className="cell-sub">{r.companySize ?? '—'}</div>
                    </td>
                    <td className="hide-mobile">{r.industry?.replace(/_/g, ' ') ?? '—'}</td>
                    <td className="hide-mobile cell-sub">{r.headquarters?.city ?? '—'}</td>
                    <td><span className={`badge ${r.scope === 'GLOBAL' ? 'badge-info' : ''}`}>{r.scope}</span></td>
                    <td>{r.mousWithThisGroup?.length ?? 0}</td>
                    <td>
                      <span className={`badge ${r.status === 'Active' ? 'badge-success' : ''}`}>{r.status ?? '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {query.data && (
            <Pagination
              page={query.data.page}
              totalPages={query.data.totalPages}
              total={query.data.total}
              limit={query.data.limit}
              onPage={(page) => setParams((p) => ({ ...p, page }))}
              onLimit={(limit) => setParams((p) => ({ ...p, limit, page: 1 }))}
            />
          )}
        </AsyncBoundary>
      </div>

      <NewPartnerDrawer
        open={newOpen}
        onClose={() => setNewOpen(false)}
        form={form}
        setForm={setForm}
        submitting={createPartner.isPending}
        errorText={createPartner.error instanceof Error ? createPartner.error.message : null}
        onSubmit={() => {
          const payload: CreatePartnerBody = {
            name: form.name.trim(),
            type: form.type,
            ...(form.industry ? { industry: form.industry } : {}),
            ...(form.companySize ? { companySize: form.companySize } : {}),
            ...(form.website ? { website: form.website } : {}),
            ...(form.description ? { description: form.description } : {}),
            admin: {
              fullName: form.admin.fullName.trim(),
              email: form.admin.email.trim().toLowerCase(),
              phone: form.admin.phone?.trim() || undefined,
            },
          };
          createPartner.mutate(payload, {
            onSuccess: (res) => {
              toast.show({ message: `${res.partner.name} onboarded. Admin invite generated.` });
              setInviteLinkModal(res.admin.inviteLink);
              setNewOpen(false);
              setForm({
                name: '', type: 'Product_Company', industry: '', companySize: '',
                website: '', description: '',
                admin: { fullName: '', email: '', phone: '' },
              });
            },
            onError: (err) => {
              toast.show({ tone: 'danger', message: err instanceof Error ? err.message : "Couldn't create the partner." });
            },
          });
        }}
      />
      {inviteLinkModal && (
        <div className="card p-4" style={{ marginTop: 16 }} data-testid="invite-link-block">
          <h3 className="text-sm font-semibold m-0 mb-1">Admin invite link (test only)</h3>
          <p className="helper m-0 mb-2">In production this reaches the admin's email. Locally, share it directly.</p>
          <code style={{ display: 'block', wordBreak: 'break-all', padding: 6, background: 'rgb(var(--bg-tertiary))', borderRadius: 6 }}>
            {inviteLinkModal}
          </code>
          <button className="btn btn-outline mt-2" onClick={() => setInviteLinkModal(null)}>Dismiss</button>
        </div>
      )}
    </div>
  );
}
